"use client";

import PageBreadCrumb from "@/components/common/PageBreadCrumb";
import FilterBar from "@/components/ui/FilterBar";
import Button from "@/components/ui/button/Button";
import DatePicker from "@/components/form/date-picker";
import DataTable from "@/components/ui/DataTable";
import StatCard from "@/components/ui/StatCard";
import SearchableSelect from "@/components/ui/SearchableSelect";
import Pagination from "@/components/tables/Pagination";
import { rmcClient } from "@/lib/rmcClient";
import { useAppStore } from "@/lib/store";
import { useQuery } from "@tanstack/react-query";
import React from "react";
import toast from "react-hot-toast";

const PAGE_SIZE = 20;

/** Delivery sites from sales parties (same pattern as sales invoices / challans). */
function buildSalesSiteOptions(parties, partyFilterId) {
  const rows = [];
  const seen = new Set();
  for (const p of parties || []) {
    if (
      partyFilterId !== undefined &&
      partyFilterId !== null &&
      partyFilterId !== "" &&
      String(p.id) !== String(partyFilterId)
    ) {
      continue;
    }
    for (const s of p.sites || []) {
      const name = (s.name || s.site_name || "").trim();
      if (!name) continue;
      const id = s.id != null && s.id !== "" ? String(s.id) : `p${p.id}::${name}`;
      if (seen.has(id)) continue;
      seen.add(id);
      rows.push({
        id,
        name,
        siteName: name,
        partyName: (p.name || "").trim(),
      });
    }
  }
  rows.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
  return [{ id: "", name: "All sites", siteName: "", partyName: "" }, ...rows];
}

function buildSiteExportParams(siteId, siteOptions) {
  if (!siteId) return {};
  const n = Number(siteId);
  if (Number.isFinite(n) && n > 0 && String(n) === String(siteId).trim()) {
    return { site_id: n };
  }
  const o = siteOptions.find((x) => String(x.id) === String(siteId));
  const name = o?.siteName?.trim();
  return name ? { site_name: name } : {};
}

function pickNonEmptyStr(...values) {
  for (const v of values) {
    if (v != null && String(v).trim() !== "") return String(v).trim();
  }
  return null;
}

/** Resolve site label from report row (API may send only site_id). */
function resolveSalesSiteLabel(row, parties = []) {
  if (!row || typeof row !== "object") return "—";

  const direct = pickNonEmptyStr(
    row.site_name,
    typeof row.site === "string" ? row.site : null,
    row.site?.name,
    row.delivery_site_name,
    row.delivery_site?.name,
    row.challan?.site_name,
    row.challan?.site?.name,
    row.sales_challan?.site_name,
    row.sales_challan?.site?.name,
  );
  if (direct) return direct;

  const siteId = row.site_id ?? row.site?.id ?? row.challan?.site_id ?? row.sales_challan?.site_id;
  if (siteId == null || siteId === "") return "—";

  const partyId = row.party_id ?? row.party?.id;
  const partyList =
    partyId != null && partyId !== ""
      ? parties.filter((p) => String(p.id) === String(partyId))
      : parties;

  for (const p of partyList) {
    const match = (p.sites || []).find((s) => String(s.id) === String(siteId));
    const name = pickNonEmptyStr(match?.name, match?.site_name);
    if (name) return name;
  }

  return "—";
}

function extractReportRows(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data?.results)) return data.data.results;
  return [];
}

/** Supports `meta` (count, next, previous, total_pages) and top-level DRF fields. */
function readPaginatedListMeta(raw) {
  if (!raw || typeof raw !== "object") {
    return { count: null, hasNext: false, hasPrevious: false, totalPages: null };
  }
  const meta = raw.meta && typeof raw.meta === "object" ? raw.meta : null;
  const tryCount = (obj) => {
    if (!obj || typeof obj !== "object") return null;
    for (const key of ["count", "total", "total_count"]) {
      const n = Number(obj[key]);
      if (Number.isFinite(n) && n >= 0) return n;
    }
    return null;
  };
  const count = tryCount(raw) ?? tryCount(raw.data) ?? tryCount(meta);
  const totalPagesNum = Number(meta?.total_pages);
  const totalPages =
    Number.isFinite(totalPagesNum) && totalPagesNum > 0 ? Math.floor(totalPagesNum) : null;
  return {
    count,
    totalPages,
    hasNext: Boolean(raw.next ?? meta?.next),
    hasPrevious: Boolean(raw.previous ?? meta?.previous),
  };
}

function readListMetaBlock(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const m = raw.meta ?? raw.data?.meta;
  return m && typeof m === "object" ? m : null;
}

export default function RmcAccountsReports() {
  const fy = useAppStore((s) => s.activeFY);
  const [reportTab, setReportTab] = React.useState("sales");
  const [from, setFrom] = React.useState("2025-04-01");
  const [to, setTo] = React.useState(new Date().toISOString().slice(0, 10));
  const [partyId, setPartyId] = React.useState("");
  const [siteId, setSiteId] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [partySummaryPage, setPartySummaryPage] = React.useState(1);
  const [applied, setApplied] = React.useState({
    from: "2025-04-01",
    to: new Date().toISOString().slice(0, 10),
    tab: "sales",
    partyId: "",
    siteId: "",
  });

  const { data: parties = [] } = useQuery({
    queryKey: ["salesParties"],
    queryFn: async () => (await rmcClient.listSalesParties()).data,
  });

  const siteOptionsForApplied = React.useMemo(
    () => buildSalesSiteOptions(parties, applied.partyId),
    [parties, applied.partyId]
  );

  const siteFilterParams = React.useMemo(
    () => buildSiteExportParams(applied.siteId, siteOptionsForApplied),
    [applied.siteId, siteOptionsForApplied]
  );

  React.useEffect(() => {
    if (!applied.siteId) return;
    const ok = siteOptionsForApplied.some((o) => o.id !== "" && String(o.id) === String(applied.siteId));
    if (!ok) setApplied((prev) => ({ ...prev, siteId: "" }));
  }, [siteOptionsForApplied, applied.siteId]);

  const salesQueryParams = React.useMemo(
    () => ({
      ...(applied.from ? { date_from: applied.from } : {}),
      ...(applied.to ? { date_to: applied.to } : {}),
      ...(applied.partyId ? { party_id: applied.partyId } : {}),
      ...siteFilterParams,
      page,
      page_size: PAGE_SIZE,
    }),
    [applied.from, applied.to, applied.partyId, siteFilterParams, page]
  );

  const partySummaryQueryParams = React.useMemo(
    () => ({
      ...(applied.from ? { date_from: applied.from } : {}),
      ...(applied.to ? { date_to: applied.to } : {}),
      ...(applied.partyId ? { party_id: applied.partyId } : {}),
      ...siteFilterParams,
      page: partySummaryPage,
      page_size: PAGE_SIZE,
    }),
    [applied.from, applied.to, applied.partyId, siteFilterParams, partySummaryPage]
  );

  const { data: salesReportData, isFetching: isSalesFetching } = useQuery({
    queryKey: ["salesReportV2", salesQueryParams],
    queryFn: async () => (await rmcClient.getSalesReport(salesQueryParams)).data,
    enabled: applied.tab === "sales",
  });

  const { data: partySummaryData, isFetching: isPartySummaryFetching } = useQuery({
    queryKey: ["salesPartySummaryV2", partySummaryQueryParams],
    queryFn: async () => (await rmcClient.getSalesPartySummaryReport(partySummaryQueryParams)).data,
    enabled: applied.tab === "party-wise",
  });

  const salesRows = React.useMemo(() => {
    const raw = extractReportRows(salesReportData);
    const toNum = (value) => {
      const n = Number(value);
      return Number.isFinite(n) ? n : 0;
    };
    return (raw || []).map((r) => ({
      ...r,
      date:
        r.date ||
        r.entry_date ||
        r.challan_date ||
        r.period ||
        r.period_label ||
        "—",
      party:
        (typeof r.party === "string" ? r.party : null) ||
        r.party?.name ||
        r.party_name ||
        "Unknown Party",
      site: resolveSalesSiteLabel(r, parties),
      grade: r.grade || r.grade_name || "—",
      qty: toNum(r.qty_cum ?? r.qty ?? r.total_qty ?? r.quantity),
      amount: toNum(r.amount ?? r.total_amount ?? r.sale_amount),
    }));
  }, [salesReportData, parties]);

  const partySummaryRows = React.useMemo(() => {
    const raw = extractReportRows(partySummaryData);
    const toNum = (value) => {
      const n = Number(value);
      return Number.isFinite(n) ? n : 0;
    };
    return (raw || []).map((r) => ({
      ...r,
      party: r.party || r.party_name || "Unknown Party",
      qty: toNum(r.total_qty_cum ?? r.total_qty ?? r.qty ?? r.qty_cum ?? r.quantity),
      amount: toNum(r.total_amount ?? r.amount ?? r.sale_amount),
      avg_rate: toNum(r.avg_rate ?? r.effective_rate ?? r.rate),
      entries: toNum(r.entries ?? r.entry_count ?? r.lines_count ?? r.count),
    }));
  }, [partySummaryData]);

  const salesPaginationMeta = React.useMemo(() => readPaginatedListMeta(salesReportData), [salesReportData]);
  const partyPaginationMeta = React.useMemo(() => readPaginatedListMeta(partySummaryData), [partySummaryData]);

  const salesListMeta = React.useMemo(() => readListMetaBlock(salesReportData), [salesReportData]);
  const partyListMeta = React.useMemo(() => readListMetaBlock(partySummaryData), [partySummaryData]);

  const salesTotalCount = React.useMemo(() => {
    if (salesPaginationMeta.count != null) return salesPaginationMeta.count;
    if (salesPaginationMeta.hasNext) {
      return (page - 1) * PAGE_SIZE + salesRows.length + 1;
    }
    return (page - 1) * PAGE_SIZE + salesRows.length;
  }, [salesPaginationMeta, page, salesRows.length]);

  const salesTotalPages = React.useMemo(() => {
    if (salesPaginationMeta.totalPages != null) {
      return Math.max(1, salesPaginationMeta.totalPages);
    }
    if (salesPaginationMeta.count != null) {
      return Math.max(1, Math.ceil(salesPaginationMeta.count / PAGE_SIZE));
    }
    if (salesPaginationMeta.hasNext) {
      return Math.max(page + 1, 1);
    }
    return Math.max(1, page);
  }, [salesPaginationMeta, page]);

  const partyTotalCount = React.useMemo(() => {
    if (partyPaginationMeta.count != null) return partyPaginationMeta.count;
    if (partyPaginationMeta.hasNext) {
      return (partySummaryPage - 1) * PAGE_SIZE + partySummaryRows.length + 1;
    }
    return (partySummaryPage - 1) * PAGE_SIZE + partySummaryRows.length;
  }, [partyPaginationMeta, partySummaryPage, partySummaryRows.length]);

  const partyTotalPages = React.useMemo(() => {
    if (partyPaginationMeta.totalPages != null) {
      return Math.max(1, partyPaginationMeta.totalPages);
    }
    if (partyPaginationMeta.count != null) {
      return Math.max(1, Math.ceil(partyPaginationMeta.count / PAGE_SIZE));
    }
    if (partyPaginationMeta.hasNext) {
      return Math.max(partySummaryPage + 1, 1);
    }
    return Math.max(1, partySummaryPage);
  }, [partyPaginationMeta, partySummaryPage]);

  React.useEffect(() => {
    setPage((p) => (p > salesTotalPages ? salesTotalPages : p));
  }, [salesTotalPages]);

  React.useEffect(() => {
    setPartySummaryPage((p) => (p > partyTotalPages ? partyTotalPages : p));
  }, [partyTotalPages]);

  const salesCurrentPage = Math.min(page, salesTotalPages);
  const partyCurrentPage = Math.min(partySummaryPage, partyTotalPages);

  const siteOptionsDraft = React.useMemo(() => buildSalesSiteOptions(parties, partyId), [parties, partyId]);

  const fmtQty = (v) =>
    Number(v || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    });
  const fmtAmt = (v) =>
    Number(v || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const metrics = React.useMemo(() => {
    const rows = reportTab === "sales" ? salesRows : partySummaryRows;
    const meta = reportTab === "sales" ? salesListMeta : partyListMeta;
    const metaQty = Number(meta?.total_qty_cum ?? meta?.total_qty_cm);
    const metaAmt = Number(meta?.total_amount);
    const totalQty =
      Number.isFinite(metaQty) && metaQty >= 0 ? metaQty : rows.reduce((s, r) => s + Number(r.qty || 0), 0);
    const totalAmount =
      Number.isFinite(metaAmt) && metaAmt >= 0
        ? metaAmt
        : rows.reduce((s, r) => s + Number(r.amount || 0), 0);
    const partySet = new Set(rows.map((r) => r.party)).size;
    const totalCount =
      reportTab === "sales"
        ? salesPaginationMeta.count ?? rows.length
        : partyPaginationMeta.count ?? rows.length;
    return {
      totalQty,
      totalAmount,
      parties: partySet,
      entries: Number.isFinite(Number(totalCount)) ? Number(totalCount) : rows.length,
    };
  }, [
    reportTab,
    salesRows,
    partySummaryRows,
    salesListMeta,
    partyListMeta,
    salesPaginationMeta.count,
    partyPaginationMeta.count,
  ]);

  const exportCsv = async () => {
    const siteOpts = buildSalesSiteOptions(parties, applied.partyId);
    const siteParams = buildSiteExportParams(applied.siteId, siteOpts);
    try {
      if (reportTab === "sales") {
        const payload = (
          await rmcClient.getSalesReport({
            ...(applied.from ? { date_from: applied.from } : {}),
            ...(applied.to ? { date_to: applied.to } : {}),
            ...(applied.partyId ? { party_id: applied.partyId } : {}),
            ...siteParams,
            pagination: false,
          })
        ).data;
        const allRows = extractReportRows(payload).map((r) => ({
          date:
            r.date ||
            r.entry_date ||
            r.challan_date ||
            r.period ||
            r.period_label ||
            "—",
          party:
            (typeof r.party === "string" ? r.party : null) ||
            r.party?.name ||
            r.party_name ||
            "Unknown Party",
          site: resolveSalesSiteLabel(r, parties),
          grade: r.grade || r.grade_name || "—",
          qty: Number(r.qty_cum ?? r.qty ?? r.total_qty ?? r.quantity) || 0,
          amount: Number(r.amount ?? r.total_amount ?? r.sale_amount) || 0,
        }));
        const lines = [["Date", "Party", "Site", "Grade", "QTY", "Amount"]];
        allRows.forEach((r) =>
          lines.push([r.date, r.party, r.site, r.grade, String(r.qty), String(r.amount)])
        );
        const csv = lines.map((x) => x.map((c) => `"${String(c).replaceAll('"', '""')}"`).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "sales-report.csv";
        a.click();
      } else {
        const payload = (
          await rmcClient.getSalesPartySummaryReport({
            ...(applied.from ? { date_from: applied.from } : {}),
            ...(applied.to ? { date_to: applied.to } : {}),
            ...(applied.partyId ? { party_id: applied.partyId } : {}),
            ...siteParams,
            pagination: false,
          })
        ).data;
        const allRows = extractReportRows(payload).map((r) => ({
          party: r.party || r.party_name || "Unknown Party",
          qty: Number(r.total_qty_cum ?? r.total_qty ?? r.qty ?? r.quantity) || 0,
          amount: Number(r.total_amount ?? r.amount ?? r.sale_amount) || 0,
          avg_rate: Number(r.avg_rate ?? r.effective_rate ?? r.rate) || 0,
          entries: Number(r.entries ?? r.entry_count ?? r.lines_count ?? r.count) || 0,
        }));
        const lines = [["Party", "Total QTY", "Total Amount", "Avg Rate", "Entries"]];
        allRows.forEach((r) =>
          lines.push([r.party, String(r.qty), String(r.amount), String(r.avg_rate), String(r.entries)])
        );
        const csv = lines.map((x) => x.map((c) => `"${String(c).replaceAll('"', '""')}"`).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "party-wise-sales-report.csv";
        a.click();
      }
    } catch {
      toast.error("Export failed.");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PageBreadCrumb pageTitle="Sales Reports" />
        <div className="flex gap-2">
          <Button
            size="sm"
            variant={reportTab === "sales" ? "primary" : "outline"}
            onClick={() => setReportTab("sales")}
          >
            Sales Report
          </Button>
          {/* <Button
            size="sm"
            variant={reportTab === "party-wise" ? "primary" : "outline"}
            onClick={() => setReportTab("party-wise")}
          >
            Party-wise Sales Report
          </Button> */}
        </div>
      </div>
      <FilterBar>
        <div className="w-full">
          <p className="mb-2 text-[10px] text-gray-500">FY: {fy?.label}</p>
          <div className="flex w-full flex-wrap items-end gap-3">
            <div>
              <p className="mb-1 text-[10px] text-gray-500">From</p>
              <div className="min-w-[170px]">
                <DatePicker
                  id="accounts-report-from"
                  defaultDate={from}
                  placeholder="From date"
                  onChange={(selectedDates, dateStr) => setFrom(dateStr || "")}
                />
              </div>
            </div>
            <div>
              <p className="mb-1 text-[10px] text-gray-500">To</p>
              <div className="min-w-[170px]">
                <DatePicker
                  id="accounts-report-to"
                  defaultDate={to}
                  placeholder="To date"
                  onChange={(selectedDates, dateStr) => setTo(dateStr || "")}
                />
              </div>
            </div>
            <div className="min-w-[220px]">
              <SearchableSelect
                label="Party"
                value={partyId}
                onChange={(v) => {
                  setPartyId(v || "");
                  setSiteId("");
                }}
                options={[{ id: "", name: "All parties" }, ...parties]}
                getLabel={(o) => o.name}
                getValue={(o) => o.id}
                getSubLabel={(p) => p.gst_number}
                placeholder="All parties"
              />
            </div>
            <div className="min-w-[220px]">
              <SearchableSelect
                label="Site"
                value={siteId}
                onChange={(v) => setSiteId(v || "")}
                options={siteOptionsDraft}
                getLabel={(o) => o.name}
                getValue={(o) => o.id}
                getSubLabel={(o) => (o.partyName && !partyId ? o.partyName : undefined)}
                placeholder="All sites"
              />
            </div>
            <div className="ml-auto flex items-end gap-2 pb-1">
              <Button
                size="sm"
                onClick={() => {
                  setApplied({
                    from,
                    to,
                    partyId,
                    siteId,
                    tab: reportTab,
                  });
                  setPage(1);
                  setPartySummaryPage(1);
                }}
              >
                Apply
              </Button>
              <Button size="sm" variant="outline" onClick={() => void exportCsv()}>
                Export CSV
              </Button>
            </div>
          </div>
        </div>
      </FilterBar>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total Qty (CUM)" value={fmtQty(metrics.totalQty)} />
        <StatCard label="Total Amount (₹)" value={fmtAmt(metrics.totalAmount)} />
        <StatCard label="Parties" value={metrics.parties} />
        <StatCard label="Entries" value={metrics.entries} />
      </div>
      <div className="rounded-lg border border-gray-800 divide-y divide-gray-800 text-xs">
        {reportTab === "sales" ? (
          <div className="overflow-hidden rounded-md border border-gray-800">
            {isSalesFetching && (
              <p className="px-3 py-2 text-gray-500">Loading report…</p>
            )}
            <table className="w-full text-left">
              <thead>
                <tr className="bg-gray-950/70 text-gray-400">
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2">Party</th>
                  <th className="px-3 py-2">Site</th>
                  <th className="px-3 py-2">Grade</th>
                  <th className="px-3 py-2 text-right">QTY (CUM)</th>
                  <th className="px-3 py-2 text-right">Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                {salesRows.map((r, i) => (
                  <tr key={r.id ?? `${r.party}-${r.date}-${i}`} className="border-t border-gray-800/70">
                    <td className="px-3 py-1.5 text-gray-300">{r.date}</td>
                    <td className="px-3 py-1.5 text-gray-200">{r.party}</td>
                    <td className="px-3 py-1.5 text-gray-200">{r.site}</td>
                    <td className="px-3 py-1.5 text-gray-200">{r.grade}</td>
                    <td className="px-3 py-1.5 text-right text-gray-200">{fmtQty(r.qty)}</td>
                    <td className="px-3 py-1.5 text-right text-gray-200">{fmtAmt(r.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex justify-end border-t border-gray-800 px-3 py-2">
              <Pagination
                currentPage={salesCurrentPage}
                totalPages={salesTotalPages}
                onPageChange={(p) => setPage(Math.max(1, Math.min(salesTotalPages, p)))}
              />
            </div>
          </div>
        ) : (
          <>
            <DataTable
              columns={[
                { key: "party", header: "Party", render: (r) => r.party },
                { key: "qty", header: "Total QTY (CUM)", render: (r) => fmtQty(r.qty) },
                { key: "amount", header: "Total Amount (₹)", render: (r) => fmtAmt(r.amount) },
                { key: "avg", header: "Avg Rate (₹)", render: (r) => (r.avg_rate ? fmtAmt(r.avg_rate) : "—") },
                { key: "entries", header: "Entries", render: (r) => r.entries || "—" },
              ]}
              data={partySummaryRows}
              isLoading={isPartySummaryFetching}
            />
            <div className="flex justify-end border-t border-gray-800 px-3 py-2">
              <Pagination
                currentPage={partyCurrentPage}
                totalPages={partyTotalPages}
                onPageChange={(p) =>
                  setPartySummaryPage(Math.max(1, Math.min(partyTotalPages, p)))
                }
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
